/**
 * Keepsy für Firmen (firmen.html, Entwurf): Preisrechner zur Mengenstaffel und Anfrageformular.
 * Preise brutto aus b2b.js, netto daraus gerechnet. Im Prototyp wird nichts verschickt,
 * im Shop würde das Formular ein Shopify-Kontaktformular.
 * @module pages/b2b
 */

import { B2B_MIN, B2B_TIERS, MAGNETIC_GROSS_CENTS, RETAIL_GROSS_CENTS, netOf, tierFor, tierPercent } from '@keepsy/b2b.js'
import { formatEuro } from '@keepsy/pricing.js'
import { isShopify, keepsyConfig } from '@keepsy/routes.js'

const euro = (cents) => formatEuro(cents / 100)

/** Staffel aus b2b.js in die Liste schreiben, damit Liste und Rechner nie auseinanderlaufen. */
function syncTiers() {
  document.querySelectorAll('[data-b2b-tier]').forEach((li) => {
    const tier = B2B_TIERS.find((t) => t.min === Number(li.dataset.b2bTier))
    if (!tier || tier.grossCents == null) return
    const price = li.querySelector('.k-b2b-tier__price > .k-tnum')
    const net = li.querySelector('[data-b2b-net-of]')
    const off = li.querySelector('.k-b2b-tier__off')
    if (price) price.textContent = euro(tier.grossCents)
    if (net) net.textContent = euro(netOf(tier.grossCents))
    if (off) off.textContent = `−${tierPercent(tier)} %`
  })
}

function initCalc() {
  const form = document.querySelector('[data-b2b-calc]')
  if (!form) return
  const qtyInput = form.querySelector('[data-b2b-qty]')
  const range = form.querySelector('[data-b2b-range]')
  const magnetic = form.querySelector('[data-b2b-magnetic]')
  const out = {
    each: form.querySelector('[data-b2b-each]'),
    net: form.querySelector('[data-b2b-net]'),
    gross: form.querySelector('[data-b2b-gross]'),
    save: form.querySelector('[data-b2b-save]'),
    note: form.querySelector('[data-b2b-note]'),
    cta: form.querySelector('[data-b2b-calc-cta]'),
  }
  const tiers = [...document.querySelectorAll('[data-b2b-tier]')]
  const formQty = document.querySelector('[data-b2b-form-qty]')
  form.querySelector('[data-b2b-magnetic-price]').textContent = euro(MAGNETIC_GROSS_CENTS)

  const render = (source) => {
    let qty = Math.round(Number(qtyInput.value) || 0)
    if (source === range) {
      qty = Number(range.value)
      qtyInput.value = String(qty)
    } else if (qty >= Number(range.min)) {
      range.value = String(Math.min(qty, Number(range.max)))
    }
    const tier = qty >= B2B_MIN ? tierFor(qty) : null
    tiers.forEach((li) => li.classList.toggle('is-active', Boolean(tier) && Number(li.dataset.b2bTier) === tier.min))
    if (formQty && tier) formQty.value = tier.min >= 250 ? '250+' : tier.min >= 100 ? '100-249' : tier.min >= 50 ? '50-99' : '20-49'

    if (!tier) {
      ;[out.each, out.net, out.gross, out.save].forEach((el) => { el.textContent = `ab ${B2B_MIN} Hüllen` })
      out.note.textContent = `Firmenpreise gibt es ab ${B2B_MIN} Hüllen. Kleinere Mengen bestellt ihr einfach im Shop, ab 4 Hüllen mit 4er-Vorteil.`
      out.cta.textContent = 'Angebot anfragen'
      return
    }
    out.cta.textContent = `Für ${qty} Hüllen anfragen`
    if (tier.grossCents == null) {
      ;[out.each, out.net, out.gross, out.save].forEach((el) => { el.textContent = 'auf Anfrage' })
      out.note.textContent = 'Ab 250 Hüllen rechnen wir individuell, mit dem besten Stückpreis.'
      return
    }
    const extra = magnetic.checked ? MAGNETIC_GROSS_CENTS : 0
    const each = tier.grossCents + extra
    const gross = each * qty
    out.each.textContent = euro(each)
    out.gross.textContent = euro(gross)
    out.net.textContent = euro(netOf(gross))
    out.save.textContent = euro((RETAIL_GROSS_CENTS + extra) * qty - gross)
    out.note.textContent = 'Unverbindliche Rechnung. Den genauen Preis bekommt ihr mit dem Angebot.'
  }

  // Tipp auf eine Staffel-Zeile übernimmt deren Mindestmenge in den Rechner
  document.querySelectorAll('[data-b2b-pick]').forEach((button) => {
    button.addEventListener('click', () => {
      qtyInput.value = button.dataset.b2bPick
      render(qtyInput)
      if (window.matchMedia('(max-width: 63.99rem)').matches) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        form.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
      }
    })
  })

  qtyInput.addEventListener('input', () => render(qtyInput))
  range.addEventListener('input', () => render(range))
  magnetic.addEventListener('change', () => render())
  form.addEventListener('submit', (event) => event.preventDefault())
  render()
}

/** Gewählte Dateien (Logo oder Motiv), gepflegt von initFiles. */
const selectedFiles = []

/** Höchstgröße aller Dateien zusammen (wie beim Foto-Upload im Konfigurator). */
const MAX_FILES_BYTES = 20 * 1024 * 1024

/**
 * Lädt Dateien im Shop hoch. Shopifys Kontaktformular nimmt keine Dateien an, Datei-Properties im Warenkorb aber
 * schon (so lädt auch der Konfigurator die Druckdatei hoch): Die Dateien gehen als Line-Item-Properties an
 * /cart/add.js, Shopify speichert sie dauerhaft und gibt die Adressen zurück. Die Zeile wird sofort wieder
 * entfernt, der Warenkorb der Kundschaft bleibt wie vorher. Die Adressen landen im Feld "Dateien" der Anfrage.
 * @param {File[]} files
 * @returns {Promise<string[]>} Datei-Adressen
 */
async function uploadFilesToShop(files) {
  const config = keepsyConfig()
  const variants = (config && config.product && config.product.variants) || []
  const variant = variants.find((v) => v.available) || variants[0]
  if (!variant) throw new Error('Kein Produkt für den Upload')
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
  const body = new FormData()
  body.append('id', String(variant.id))
  body.append('quantity', '1')
  files.forEach((file, i) => body.append(`properties[_Anfrage-Datei-${i + 1}]`, file, file.name))
  const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
  const res = await fetch(`${root}cart/add.js`, { method: 'POST', body, headers })
  if (!res.ok) throw new Error(`Upload ${res.status}`)
  const item = await res.json()
  if (item && item.key) {
    await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 }),
    }).catch(() => {})
  }
  const urls = Object.entries((item && item.properties) || {})
    .filter(([key]) => key.startsWith('_Anfrage-Datei'))
    .map(([, value]) => String(value))
  if (urls.length !== files.length) throw new Error('Nicht alle Dateien hochgeladen')
  return urls.map((url) => (url.startsWith('//') ? `https:${url}` : url))
}

function initForm() {
  const form = document.querySelector('[data-b2b-form], #k-b2b-form')
  if (!form) return
  const ok = form.querySelector('[data-b2b-form-ok]')
  const topics = [...form.querySelectorAll('[data-b2b-topic-input]')]
  const groups = [...form.querySelectorAll('[data-b2b-for]')]
  // Je Thema nur die passenden Felder: Hüllen fragen Menge und Modelle, der Automat Gäste, Datum, Ort und Folierung.
  // Versteckte Felder sind deaktiviert, damit sie weder geprüft noch mitgeschickt werden.
  const syncTopic = () => {
    const checked = topics.find((input) => input.checked) || topics[0]
    const topic = checked.dataset.topic || checked.value
    groups.forEach((group) => {
      const active = group.dataset.b2bFor === topic
      group.hidden = !active
      group.querySelectorAll('input, select, textarea').forEach((el) => { el.disabled = !active })
    })
  }
  topics.forEach((input) => input.addEventListener('change', syncTopic))
  // "Automat anfragen" wählt im Formular gleich das passende Thema
  document.querySelectorAll('[data-b2b-topic]').forEach((link) => {
    link.addEventListener('click', () => {
      const input = topics.find((i) => (i.dataset.topic || i.value) === link.dataset.b2bTopic)
      if (input) input.checked = true
      syncTopic()
    })
  })

  // Im Shop schickt das Shopify-Kontaktformular die Anfrage ab, im Prototyp bleibt alles lokal
  form.addEventListener('submit', (event) => {
    const missing = [...form.querySelectorAll('[required]:not(:disabled)')].filter((el) => !String(el.value).trim() || (el.type === 'email' && !el.checkValidity()))
    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'))
    if (missing.length) {
      event.preventDefault()
      ok.hidden = false
      missing.forEach((el) => el.setAttribute('aria-invalid', 'true'))
      missing[0].focus()
      ok.dataset.tone = 'error'
      ok.textContent = 'Fehler: Bitte füllt die markierten Felder aus.'
      return
    }
    const total = selectedFiles.reduce((sum, file) => sum + file.size, 0)
    if (total > MAX_FILES_BYTES) {
      event.preventDefault()
      ok.hidden = false
      ok.dataset.tone = 'error'
      ok.textContent = 'Fehler: Die Dateien sind zusammen größer als 20 MB. Entfernt eine oder schickt sie per E-Mail.'
      return
    }
    if (isShopify()) {
      const urlField = form.querySelector('[data-b2b-file-urls]')
      if (!selectedFiles.length || !urlField || form.dataset.uploaded === 'true') return
      // Erst die Dateien hochladen, dann das Formular mit den Adressen abschicken
      event.preventDefault()
      const submit = form.querySelector('[type="submit"]')
      if (submit) submit.disabled = true
      ok.hidden = false
      ok.dataset.tone = 'info'
      ok.textContent = 'Dateien werden hochgeladen.'
      uploadFilesToShop(selectedFiles)
        .then((urls) => {
          urlField.value = urls.join('\n')
          form.dataset.uploaded = 'true'
          form.submit()
        })
        .catch(() => {
          if (submit) submit.disabled = false
          ok.dataset.tone = 'error'
          ok.textContent = 'Fehler: Die Dateien konnten nicht hochgeladen werden. Entfernt sie und schickt die Anfrage ohne, das Logo dann einfach per E-Mail an info@keepsy-case.de.'
        })
      return
    }
    event.preventDefault()
    ok.hidden = false
    ok.dataset.tone = 'ok'
    ok.textContent = 'Danke! Im Prototyp wird nichts verschickt. Im Shop landet die Anfrage bei info@keepsy-case.de.'
  })
  syncTopic()
}

/** Logo oder Motiv als Datei: Liste mit Entfernen, Größengrenze, Ziehen und Ablegen. */
function initFiles() {
  const input = document.querySelector('[data-b2b-files]')
  if (!input) return
  const list = document.querySelector('[data-b2b-file-list]')
  const error = document.querySelector('[data-b2b-file-error]')
  const drop = document.querySelector('.k-b2b-file__drop')
  const files = selectedFiles
  const size = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`)
  const render = () => {
    list.hidden = files.length === 0
    list.innerHTML = files.map((file, i) => `<li><span>${file.name.replace(/[<>&"]/g, '')} <span class="k-muted">${size(file.size)}</span></span><button type="button" data-remove="${i}">Entfernen</button></li>`).join('')
    const total = files.reduce((sum, file) => sum + file.size, 0)
    error.hidden = total <= MAX_FILES_BYTES
    error.textContent = error.hidden ? '' : `Fehler: Zusammen ${size(total)}, erlaubt sind 20 MB. Schickt größere Dateien bitte per E-Mail.`
  }
  const add = (incoming) => {
    files.push(...[...incoming].filter((file) => /^(image\/(png|jpeg|svg\+xml)|application\/pdf)$/.test(file.type) || /\.(svg|pdf)$/i.test(file.name)))
    render()
  }
  input.addEventListener('change', () => {
    add(input.files)
    input.value = ''
  })
  list.addEventListener('click', (event) => {
    const button = event.target.closest('[data-remove]')
    if (!button) return
    files.splice(Number(button.dataset.remove), 1)
    render()
    input.focus()
  })
  ;['dragenter', 'dragover'].forEach((type) => drop.addEventListener(type, (event) => {
    event.preventDefault()
    drop.classList.add('is-over')
  }))
  ;['dragleave', 'drop'].forEach((type) => drop.addEventListener(type, () => drop.classList.remove('is-over')))
  drop.addEventListener('drop', (event) => {
    event.preventDefault()
    if (event.dataTransfer && event.dataTransfer.files) add(event.dataTransfer.files)
  })
}

syncTiers()
initCalc()
initForm()
initFiles()
